import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  ENTITY_LABELS,
  FIELD_LABELS,
  SOURCE_LABELS,
  conflictCode,
  createAssignment,
  plDay,
  searchContractors,
  updateAssignment,
  type FuelSurchargeAssignment,
  type FuelSurchargeChange,
  type FuelSurchargeType,
} from './fuelSurchargesApi';

/**
 * Wspólne części kartoteki korekt paliwowych (B4, 21.10.2026): tabela
 * przypisań, okna przypisania, zakończenia i wycofania, historia. Używa ich
 * ekran „Korekty paliwowe” i sekcja na karcie kontrahenta.
 */

const DATE_SLOT = { inputLabel: { shrink: true } } as const;

/** Okno z powodem — wycofanie wartości albo przypisania. */
export const ReasonDialog = ({
  open,
  title,
  text,
  confirmLabel,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  text: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{text}</DialogContentText>
        <TextField
          label="Powód"
          required
          fullWidth
          multiline
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'fuel-reason' } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          disabled={reason.trim() === '' || busy}
          onClick={() => {
            setBusy(true);
            void onConfirm(reason.trim()).finally(() => setBusy(false));
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Przypisanie kontrahenta do rodzaju korekty. Kontrahent stały (karta
 * kontrahenta) albo wyszukany w kartotece. Gdy kontrahent ma w tym czasie
 * inną korektę, serwer pyta (409 `previous`) — okno pokazuje pytanie
 * i pole „Zakończ poprzednie przypisanie”; bez niego nic nie zapisuje.
 */
export const AssignDialog = ({
  open,
  onClose,
  onSaved,
  types,
  contractor,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  types: FuelSurchargeType[];
  contractor?: { id: number; legalName: string };
}) => {
  const notify = useNotify();
  const [contractorId, setContractorId] = useState<number | ''>(contractor?.id ?? '');
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<Array<{ id: number; legalName: string; tmsCustomerId: number | null; taxId: string | null }>>([]);
  const [typeId, setTypeId] = useState<number | ''>('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [askEnd, setAskEnd] = useState<string | null>(null);
  const [endPrevious, setEndPrevious] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setContractorId(contractor?.id ?? '');
    setSearch('');
    setFound([]);
    setTypeId('');
    setValidFrom('');
    setValidTo('');
    setNote('');
    setReason('');
    setAskEnd(null);
    setEndPrevious(false);
  }, [open, contractor?.id]);

  const find = async () => {
    try {
      setFound(await searchContractors(search.trim()));
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    }
  };

  const canSave =
    contractorId !== '' && typeId !== '' && validFrom !== '' && reason.trim() !== '' && (!askEnd || endPrevious);

  const save = async () => {
    setBusy(true);
    try {
      const out = await createAssignment({
        contractorId: contractorId as number,
        typeId: typeId as number,
        validFrom,
        validTo: validTo || null,
        note: note.trim() || undefined,
        reason: reason.trim(),
        endPrevious,
      });
      notify(
        out.endedPrevious
          ? `Przypisano — poprzednia korekta kończy się ${plDay(out.endedPrevious.validTo)}`
          : 'Przypisano korektę',
        { type: 'success' },
      );
      onSaved();
    } catch (e) {
      if (conflictCode(e) === 'previous') setAskEnd(toErrorMessage(e));
      else notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const active = types.filter((t) => t.active);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Przypisz korektę paliwową</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {contractor ? (
            <Typography variant="body2">Kontrahent: {contractor.legalName}</Typography>
          ) : (
            <>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Szukaj kontrahenta (nazwa, NIP)"
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void find();
                  }}
                  sx={{ flex: 1 }}
                  slotProps={{ htmlInput: { 'data-testid': 'fuel-contractor-search' } }}
                />
                <Button onClick={() => void find()} disabled={search.trim() === ''}>
                  Szukaj
                </Button>
              </Stack>
              <TextField
                select
                label="Kontrahent"
                value={contractorId}
                onChange={(e) => setContractorId(Number(e.target.value))}
                disabled={found.length === 0}
                helperText={found.length === 0 ? 'Najpierw wyszukaj kontrahenta' : undefined}
                slotProps={{ select: { SelectDisplayProps: { 'data-testid': 'fuel-contractor-select' } as object } }}
              >
                {found.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.legalName}
                    {c.tmsCustomerId ? ` · TMS ${c.tmsCustomerId}` : ''}
                    {c.taxId ? ` · NIP ${c.taxId}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
          <TextField
            select
            label="Rodzaj korekty"
            value={typeId}
            onChange={(e) => setTypeId(Number(e.target.value))}
            slotProps={{ select: { SelectDisplayProps: { 'data-testid': 'fuel-type-select' } as object } }}
          >
            {active.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={1}>
            <TextField
              type="date"
              label="Obowiązuje od"
              required
              value={validFrom}
              onChange={(e) => {
                setValidFrom(e.target.value);
                setAskEnd(null);
                setEndPrevious(false);
              }}
              slotProps={{ ...DATE_SLOT, htmlInput: { 'data-testid': 'fuel-assign-from' } }}
            />
            <TextField
              type="date"
              label="Obowiązuje do (puste = bez końca)"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              slotProps={DATE_SLOT}
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField label="Uwagi" value={note} onChange={(e) => setNote(e.target.value)} slotProps={{ htmlInput: { maxLength: 500 } }} />
          <TextField
            label="Powód (np. plik od CS z dnia…)"
            required
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'fuel-assign-reason' } }}
          />
          {askEnd && (
            <Alert severity="warning" data-testid="fuel-assign-previous">
              {askEnd}
              <FormControlLabel
                sx={{ display: 'block', mt: 1 }}
                control={<Checkbox checked={endPrevious} onChange={(e) => setEndPrevious(e.target.checked)} />}
                label="Zakończ poprzednie przypisanie dzień przed nowym"
              />
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" disabled={!canSave || busy} onClick={() => void save()}>
          Przypisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Data końca przypisania (puste = bez końca) z powodem. */
export const EndAssignmentDialog = ({
  assignment,
  onClose,
  onSaved,
}: {
  assignment: FuelSurchargeAssignment | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const notify = useNotify();
  const [validTo, setValidTo] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setValidTo(assignment?.validTo ?? '');
    setReason('');
  }, [assignment]);
  const save = async () => {
    if (!assignment) return;
    setBusy(true);
    try {
      await updateAssignment(assignment.id, { validTo: validTo || null, reason: reason.trim() });
      notify('Zapisano datę końca', { type: 'success' });
      onSaved();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={assignment !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Data końca przypisania</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            {assignment ? `${assignment.contractor.legalName} → ${assignment.type.name}, od ${plDay(assignment.validFrom)}` : ''}
          </Typography>
          <TextField
            type="date"
            label="Obowiązuje do (puste = bez końca)"
            value={validTo}
            onChange={(e) => setValidTo(e.target.value)}
            slotProps={DATE_SLOT}
          />
          <TextField
            label="Powód"
            required
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          disabled={reason.trim() === '' || busy || (assignment?.validTo ?? '') === validTo}
          onClick={() => void save()}
        >
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Tabela przypisań; wycofane wyszarzone z powodem. */
export const AssignmentsTable = ({
  rows,
  canEdit,
  showContractor,
  onEnd,
  onWithdraw,
}: {
  rows: FuelSurchargeAssignment[];
  canEdit: boolean;
  showContractor: boolean;
  onEnd: (a: FuelSurchargeAssignment) => void;
  onWithdraw: (a: FuelSurchargeAssignment) => void;
}) => {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="fuel-assignments-empty">
        Brak przypisań.
      </Typography>
    );
  }
  return (
    <Table size="small" data-testid="fuel-assignments">
      <TableHead>
        <TableRow>
          {showContractor && <TableCell>Kontrahent</TableCell>}
          <TableCell>Rodzaj korekty</TableCell>
          <TableCell>Obowiązuje</TableCell>
          <TableCell>Źródło</TableCell>
          <TableCell>Dodał</TableCell>
          {canEdit && <TableCell />}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((a) => (
          <TableRow key={a.id} data-testid="fuel-assignment-row" sx={a.withdrawnAt ? { opacity: 0.6 } : undefined}>
            {showContractor && (
              <TableCell>
                {a.contractor.legalName}
                {a.contractor.tmsCustomerId ? (
                  <Typography variant="caption" color="text.secondary" component="div">
                    TMS {a.contractor.tmsCustomerId}
                  </Typography>
                ) : null}
              </TableCell>
            )}
            <TableCell>
              {a.type.name}
              {a.withdrawnAt ? (
                <Chip size="small" variant="outlined" label="wycofane" sx={{ ml: 1 }} title={a.withdrawnReason ?? undefined} />
              ) : null}
            </TableCell>
            <TableCell>
              od {plDay(a.validFrom)}
              {a.validTo ? ` do ${plDay(a.validTo)}` : ', bez daty końca'}
            </TableCell>
            <TableCell>
              {SOURCE_LABELS[a.source] ?? a.source}
              {a.importId ? ` (#${a.importId})` : ''}
            </TableCell>
            <TableCell>
              {a.createdByName ?? '—'}
              <Typography variant="caption" color="text.secondary" component="div">
                {dateTimeNoSeconds(a.createdAt)}
              </Typography>
            </TableCell>
            {canEdit && (
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {a.withdrawnAt ? null : (
                  <>
                    <Button size="small" onClick={() => onEnd(a)}>
                      Data końca
                    </Button>
                    <Button size="small" color="warning" onClick={() => onWithdraw(a)}>
                      Wycofaj
                    </Button>
                  </>
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/** Historia kartoteki: kto, kiedy, czego dotyczy, z czego na co, powód. */
export const HistoryTable = ({
  rows,
  typeName,
}: {
  rows: FuelSurchargeChange[];
  typeName: (id: number | null) => string;
}) =>
  rows.length === 0 ? (
    <Typography variant="body2" color="text.secondary">
      Bez zmian.
    </Typography>
  ) : (
    <Table size="small" aria-label="Historia zmian" data-testid="fuel-history">
      <TableHead>
        <TableRow>
          <TableCell>Kiedy</TableCell>
          <TableCell>Kto</TableCell>
          <TableCell>Czego dotyczy</TableCell>
          <TableCell>Zmiana</TableCell>
          <TableCell>Powód</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((h) => (
          <TableRow key={h.id}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTimeNoSeconds(h.changedAt)}</TableCell>
            <TableCell>{h.changedByName ?? '—'}</TableCell>
            <TableCell>
              {ENTITY_LABELS[h.entity] ?? h.entity}
              {h.typeId ? ` · ${typeName(h.typeId)}` : ''}
            </TableCell>
            <TableCell sx={{ wordBreak: 'break-word' }}>
              {FIELD_LABELS[h.field] ?? h.field}
              {h.field === 'created' ? `: ${h.newValue ?? ''}` : `: ${h.oldValue ?? '—'} → ${h.newValue ?? '—'}`}
            </TableCell>
            <TableCell sx={{ wordBreak: 'break-word' }}>{h.reason}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
